import { Wordmark } from "./Wordmark";
import { Link } from "@tanstack/react-router";
import { useI18n } from "@/i18n/I18nProvider";
import { Facebook, Instagram, Linkedin, ArrowRight } from "lucide-react";

const SOCIALS = [
  {
    href: "https://www.facebook.com/heallexa",
    label: "Facebook",
    Icon: Facebook,
    brand: "#1877F2",
    hoverBg: "#1877F2",
  },
  {
    href: "https://www.instagram.com/heallexa",
    label: "Instagram",
    Icon: Instagram,
    brand: "#E1306C",
    hoverBg:
      "linear-gradient(45deg, #F58529 0%, #DD2A7B 45%, #8134AF 75%, #515BD4 100%)",
  },
  {
    href: "https://x.com/heallexahq",
    label: "X (Twitter)",
    Icon: XIcon,
    brand: "#000000",
    hoverBg: "#000000",
  },
  {
    href: "https://www.linkedin.com/company/heallexa",
    label: "LinkedIn",
    Icon: Linkedin,
    brand: "#0A66C2",
    hoverBg: "#0A66C2",
  },
] as const;

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.65l-5.214-6.817L4.99 21.75H1.68l7.73-8.836L1.254 2.25H8.08l4.71 6.231 5.454-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
    </svg>
  );
}

export function FooterSection() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-border bg-card">
      <div className="mx-auto max-w-6xl px-6 pt-10 pb-6">
        {/* Brand row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 pb-8">
          <div className="md:col-span-4">
            <Wordmark className="text-lg mb-3" />
            <p className="text-sm text-muted-foreground max-w-[16rem] mb-4 leading-snug">
              {t.footer.tagline}
            </p>
            <div className="text-sm text-muted-foreground space-y-1 mb-4">
              <a href="mailto:contact@heallexa.com" className="block hover:text-foreground transition-colors">
                contact@heallexa.com
              </a>
              <a href="tel:+14708000081" className="block hover:text-foreground transition-colors">
                (470) 800-0081
              </a>
            </div>
            <div className="flex items-center gap-2">
              {SOCIALS.map(({ href, label, Icon, brand, hoverBg }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Heallexa on ${label}`}
                  style={{ color: brand }}
                  className="group relative inline-flex h-8 w-8 items-center justify-center border border-border transition-colors hover:text-white hover:border-transparent"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = hoverBg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "";
                  }}
                >
                  <Icon className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            <FooterCol title={t.footer.patients}>
              <FooterLink to="/">{t.footer.home}</FooterLink>
              <FooterLink to="/browse">Find a Doctor</FooterLink>
              <FooterLink to="/search">{t.footer.searchLink}</FooterLink>
              <FooterMore to="/about" label="About Heallexa" />
            </FooterCol>

            <FooterCol title="Browse">
              <FooterLink to="/specialties">Specialties</FooterLink>
              <FooterLink to="/locations">Locations</FooterLink>
              <FooterLink to="/insurance">Insurance</FooterLink>
              <FooterMore to="/doctors/directory" label="Full directory" />
            </FooterCol>

            <FooterCol title="Compare">
              <FooterLink to="/compare">Compare Heallexa</FooterLink>
              <FooterLinkParams slug="heallexa-vs-zocdoc">vs Zocdoc</FooterLinkParams>
              <FooterLinkParams slug="heallexa-vs-healthgrades">vs Healthgrades</FooterLinkParams>
              <FooterMore to="/compare" label="All comparisons" />
            </FooterCol>

            <FooterCol title={t.footer.providers}>
              <FooterLink to="/list-your-practice">{t.nav.list}</FooterLink>
              <FooterLink to="/browse">{t.footer.claim}</FooterLink>
              <FooterLink to="/login">{t.footer.providerLogin}</FooterLink>
              <FooterMore to="/list-your-practice" label="Provider plans" />
            </FooterCol>
          </div>
        </div>

        {/* Legal row */}
        <div className="pt-5 border-t border-border flex flex-col md:flex-row gap-3 md:gap-5 justify-between items-start md:items-center text-xs text-muted-foreground">
          <span className="leading-relaxed shrink-0">
            &copy; {new Date().getFullYear()} Heallexa Inc. {t.footer.rights}
          </span>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-foreground transition-colors">{t.footer.privacy}</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">{t.footer.terms}</Link>
            <a href="/sitemap" className="hover:text-foreground transition-colors">Sitemap</a>
            <Link to="/admin-login" className="opacity-50 hover:opacity-100 hover:text-foreground transition-opacity">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}


function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-[11px] font-semibold text-foreground uppercase tracking-[0.14em] mb-3">
        {title}
      </h4>
      <ul className="space-y-2 text-sm text-muted-foreground">{children}</ul>
    </div>
  );
}

const linkClass =
  "inline-block text-muted-foreground hover:text-foreground transition-colors relative after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-0 after:bg-foreground after:transition-all hover:after:w-full";

function FooterLink(props: React.ComponentProps<typeof Link>) {
  return (
    <li>
      <Link {...props} className={linkClass} />
    </li>
  );
}

function FooterLinkParams({ slug, children }: { slug: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to="/compare/$slug" params={{ slug }} className={linkClass}>
        {children}
      </Link>
    </li>
  );
}

/**
 * Footer column "see more" link.
 * Accepts a descriptive `label` so the anchor text is meaningful to crawlers
 * rather than the generic "See more" used 38× across the site.
 */
function FooterMore({ to, label }: { to: string; label: string }) {
  return (
    <li className="pt-1">
      <Link to={to} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary hover:text-primary/80 transition-colors">
        {label} <ArrowRight className="w-3 h-3" />
      </Link>
    </li>
  );
}
